/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useQuery } from "@apollo/client";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { restaurantList, getCuisines, activePromotionsForCustomer, myLoyaltyPoints } from "../../apollo/server";
import FlashMessage from "../../components/FlashMessage";
import { Header, LoginHeader } from "../../components/Header";
import { ClearCart } from "../../components/Modals";
import DealBanners from "../../components/RestaurantDetailComponent/Storefront/DealBanners";
import CuisineRow, { cuisineChoices, matchesChoice } from "../../components/RestaurantList/CuisineRow";
import OrderAgain from "../../components/RestaurantList/OrderAgain";
import MostOrderedNearby from "../../components/RestaurantList/MostOrderedNearby";
import AllRestaurants from "../../components/RestaurantList/AllRestaurants";
import { prepMinutesOf } from "../../components/RestaurantList/RestaurantListCard";
import PickupHome from "../../components/PickupNearby/PickupHome";
import { addressToLocation, nothingDelivers } from "../../components/PickupNearby/pickupText";

import { useLocationContext } from "../../context/Location";
import UserContext from "../../context/User";
import { useOrderMode, ORDER_MODES } from "../../context/OrderMode";
import { useSearch } from "../../context/Search";
import Analytics from "../../utils/analytics";
import { perfMark, perfSpan, perfCount } from "../../utils/perfDebug";
import { isRestaurantOpenNow } from "../../utils/helper";
import { cityLine } from "../../utils/shortAddress";
import { C, TYPE, topBarOffset } from "../../utils/designTokens";
import useStyles from "./styles";
import Seo from "../../components/Seo/Seo";
import { breadcrumbSchema } from "../../utils/seoSchema";

const RESTAURANTS = gql`
  ${restaurantList}
`;

const GET_CUISINES = gql`
  ${getCuisines}
`;

const GET_ACTIVE_PROMOS = gql`
  ${activePromotionsForCustomer}
`;

const MY_LOYALTY = gql`
  ${myLoyaltyPoints}
`;

const NO_FILTERS = { deals: false, favourites: false, quick: false };

const byDistance = (a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity);

// The list for both modes. `pickupPage` is /pickup: always the pickup list
// (by enablePickup), with its own page title, and the site in pickup mode.
function Restaurants({ pickupPage = false }) {
  const navigate = useNavigate();
  // ?open=now: only restaurants taking orders now (the closed-restaurant
  // popup's "See restaurants open now", and the Open now chip).
  const openNowOnly = new URLSearchParams(useLocation().search).get("open") === "now";
  const { location, setLocation } = useLocationContext();
  const [message, setMessage] = useState({});
  const { search, setSearch } = useSearch();
  const { isLoggedIn, profile, stashCart, restaurant: cartRestaurant } = useContext(UserContext);
  const [clearModal, setClearModal] = useState(false);
  const [navigateData, setNavigateData] = useState({});
  const [choiceKey, setChoiceKey] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sort, setSort] = useState("nearest");
  const classes = useStyles();
  // `orders` is deliberately not read from UserContext here: that is the
  // megabyte-sized myOrders query. Order again asks for its own few rows.
  const { orderMode, setOrderMode } = useOrderMode();
  const isPickup = pickupPage || orderMode === ORDER_MODES.PICKUP;
  const basePath = pickupPage ? "/pickup" : "/restaurant-list";
  // Landing on /pickup straight from a link: the header toggle, cart and
  // checkout follow the mode, so put the site in pickup too. Only on arrival:
  // switching to Delivery from here changes the mode, then leaves the page.
  useEffect(() => {
    if (pickupPage && orderMode !== ORDER_MODES.PICKUP) setOrderMode(ORDER_MODES.PICKUP);
  }, [pickupPage]);

  const navigateClearCart = useCallback(async () => {
    // Park it rather than bin it — the customer can come back to this cart.
    await stashCart();
    navigate(navigateData.path, { state: navigateData.state });
  }, [navigateData]);
  useEffect(async () => {
    await Analytics.track(Analytics.events.NAVIGATE_TO_RESTAURANTS);
  }, []);

  // Clear search when leaving this page
  useEffect(() => {
    return () => setSearch('');
  }, []);

  const toggleClearCart = useCallback(() => {
    setClearModal((prev) => !prev);
  }, []);

  // Opens a restaurant (search: e.g. "?item=12" to open one dish). A cart from
  // another restaurant asks first, and is parked if the customer goes ahead.
  const openRestaurant = useCallback(
    (r, { search: query = "", skipCartCheck = false } = {}) => {
      const path = `/restaurant/${r.slug}${query}`;
      const state = { id: r._id, name: r.name, image: r.image, slug: r.slug };
      if (!skipCartCheck && cartRestaurant && String(r._id) !== String(cartRestaurant)) {
        setNavigateData({ path, state });
        toggleClearCart();
        return;
      }
      navigate(path, { state });
    },
    [cartRestaurant]
  );

  const showMessage = useCallback((messageObj) => {
    setMessage(messageObj);
  }, []);
  const toggleSnackbar = useCallback(() => {
    setMessage({});
  }, []);

  // Milestones for the perf panel. The list query is gated on `location`, so
  // the gap between "mounted" and "location ready" is dead time the customer
  // spends on a spinner before a single byte has been asked for — worth seeing
  // separately from the query's own cost.
  useEffect(() => { perfMark("restaurant list mounted"); }, []);
  useEffect(() => { if (location) perfMark("location ready, list query unblocked"); }, [location]);

  // The header's SearchDropdown runs this exact query too, three milliseconds
  // ahead of this one. Apollo collapses concurrent identical operations, but it
  // keys that on the variables — and `ip: null` (which the query document never
  // declares, so the server ignores it) made the two keys differ. The page then
  // paid for the whole list TWICE: two 48KB responses, thirty SQL statements,
  // and two runs of a resolver that formats 1433 menu items. Keep these
  // variables identical to the dropdown's or the duplicate comes straight back.
  const { data, loading, error } = useQuery(RESTAURANTS, {
    variables: {
      longitude: location?.longitude || null,
      latitude: location?.latitude || null,
    },
    fetchPolicy: "network-only",
    skip: !location,
  });
  useEffect(() => {
    if (!data?.nearByRestaurants) return;
    perfMark("list data ready, rendering cards");

    // What actually arrived. `menu items received` is the payload that exists
    // only so searchRestaurants below can regex over dish names — if it is in
    // the thousands, the search belongs on the server.
    const list = data.nearByRestaurants.restaurants || [];
    perfCount("restaurants received", list.length);
    perfCount(
      "menu items received",
      list.reduce((n, r) => n + (r.advancedMenus || []).reduce((m, mn) => m + (mn.items || []).length, 0), 0)
    );

    // Two frames: the first is scheduled before React commits, the second runs
    // after the browser has actually painted the cards. That second timestamp
    // is the number the customer experiences, and it is usually well past the
    // moment the data landed.
    requestAnimationFrame(() => requestAnimationFrame(() => perfMark("cards painted")));
  }, [data]);

  const { data: cuisineData } = useQuery(GET_CUISINES, { fetchPolicy: "cache-first" });
  // Same variables as the sidebar's Deals badge and the Deals page, so the
  // banners and the cards' deal counts agree with both.
  const { data: promoData } = useQuery(GET_ACTIVE_PROMOS, {
    variables: { latitude: location?.latitude || null, longitude: location?.longitude || null, pickup: isPickup },
    fetchPolicy: "cache-first",
  });
  const { data: loyaltyData } = useQuery(MY_LOYALTY, { skip: !isLoggedIn, fetchPolicy: "cache-first" });

  const allRestaurants = data?.nearByRestaurants?.restaurants ?? [];
  // Both of these re-run on every render of this screen, not just when the data
  // changes, so they are measured rather than assumed cheap.
  const delivers = (r) => r.enableDelivery !== false || r.selfDelivery === true; // self-delivery restaurants always show for delivery
  const restaurants = perfSpan("filter by order mode", () =>
    allRestaurants.filter((r) => (isPickup ? r.enablePickup !== false : delivers(r)))
  );
  // Nothing delivers to this address: the page becomes the pickup view
  // (components/PickupNearby), as the app's Home does. In pickup mode it
  // also takes over when the list has no pickup restaurant, because pickup
  // reaches past the delivery zones this list is cut to.
  const noDelivery = nothingDelivers(allRestaurants.filter(delivers));
  const showPickupHome = Boolean(location && data?.nearByRestaurants) && (noDelivery || (isPickup && restaurants.length === 0));
  // "Order for pickup" puts the site in pickup mode, so the cart and checkout
  // follow; "Use this" moves to a saved address that does get delivery.
  const orderForPickup = (r) => {
    setOrderMode(ORDER_MODES.PICKUP);
    openRestaurant(r);
  };
  const switchToAddress = (a) => {
    setOrderMode(ORDER_MODES.DELIVERY);
    setLocation(addressToLocation(a));
  };

  const promos = promoData?.activePromotionsForCustomer || [];
  const dealCounts = useMemo(() => {
    const m = new Map();
    promos.forEach((p) => p.restaurantId && m.set(String(p.restaurantId), (m.get(String(p.restaurantId)) || 0) + 1));
    return m;
  }, [promos]);
  // Platform deals (the welcome offer) first, then each restaurant's.
  const bannerPromos = useMemo(() => [...promos].sort((a, b) => Number(Boolean(a.restaurantId)) - Number(Boolean(b.restaurantId))), [promos]);
  const rewardsBalance = loyaltyData?.myLoyaltyPoints?.balance;

  const choices = useMemo(() => cuisineChoices(restaurants, cuisineData?.getCuisines ?? []), [restaurants.length, data, cuisineData]);
  const choice = choices.find((c) => c.key === choiceKey) || null;
  const favourites = profile?.favourite || [];

  const searchRestaurants = (searchText) => {
    const escaped = searchText.replace(/[\\[\]()+?.*]/g, (c) => "\\" + c);
    const regex = new RegExp(escaped, "i");
    return restaurants.filter((restaurant) => {
      // Restaurant name
      if (regex.test(restaurant.name)) return true;
      // Restaurant-level cuisine tags
      if (restaurant.tags?.some((tag) => regex.test(tag))) return true;
      if (restaurant.cuisines?.some((c) => regex.test(c))) return true;
      // Advanced-menu item names / descriptions / category labels
      const inMenu = (restaurant.advancedMenus || []).some((menu) =>
        (menu.items || []).some((item) =>
          regex.test(item.name || "") ||
          regex.test(item.description || "") ||
          regex.test(item.categoryName || "")
        )
      );
      return inMenu;
    });
  };

  const shown = (search
    ? perfSpan("search filter over menu items", () => searchRestaurants(search))
    : restaurants.filter((r) => matchesChoice(r, choice))
  )
    .filter((r) => !openNowOnly || isRestaurantOpenNow(r))
    .filter((r) => !filters.deals || (dealCounts.get(String(r._id)) || 0) > 0)
    .filter((r) => !filters.favourites || favourites.includes(r._id))
    .filter((r) => !filters.quick || prepMinutesOf(r) < 15)
    .sort((a, b) => {
      if (sort === "fastest") return prepMinutesOf(a) - prepMinutesOf(b) || byDistance(a, b);
      if (sort === "deals") return (dealCounts.get(String(b._id)) || 0) - (dealCounts.get(String(a._id)) || 0) || byDistance(a, b);
      return byDistance(a, b);
    });

  const onFilter = (key) => {
    if (key === "openNow") {
      navigate(openNowOnly ? basePath : `${basePath}?open=now`, { replace: true });
      return;
    }
    setFilters((f) => ({ ...f, [key]: !f[key] }));
  };
  const clearFilters = () => {
    setChoiceKey(null);
    setFilters(NO_FILTERS);
    if (search) setSearch("");
    if (openNowOnly) navigate(basePath, { replace: true });
  };

  if (loading || error) {
    return (
      <Grid container>
        {isLoggedIn ? <Header /> : <LoginHeader showIcon />}

        <Box className={classes.spinnerContainer}>
          {loading ? (
            <CircularProgress color="primary" size={48} />
          ) : (
            <Typography>Unable to load data</Typography>
          )}
        </Box>
      </Grid>
    );
  }

  const firstName = (profile?.name || "").trim().split(/\s+/)[0];
  const place = cityLine(location?.deliveryAddress || location?.label || "");
  const town = place.split(",")[0];
  // "Good afternoon, Vivek" by the customer's own clock (Vivek, 2026-10-29:
  // no "what are you craving?" and no "Delivering to … · N restaurants" line).
  const hour = new Date().getHours();
  const partOfDay = hour >= 5 && hour < 12 ? "Good morning" : hour >= 12 && hour < 17 ? "Good afternoon" : "Good evening";
  const gridTitle = search ? `Results for “${search}”` : choice ? choice.title : "All restaurants";

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: C.bg }}>
      {pickupPage ? (
        <Seo
          title="Pickup Near You — Skip the Delivery Fee"
          description="Find local restaurants offering pickup on RushMyFood. Order ahead, skip the delivery fee, and collect your food when it's ready."
          path="/pickup"
        />
      ) : (
        <Seo
          title="Restaurants Near You — Order Delivery & Pickup"
          description="Browse local restaurants delivering near you on RushMyFood. Compare menus, delivery times and deals, then order for delivery or pickup."
          path="/restaurant-list"
          schema={breadcrumbSchema([{ name: "Restaurants", path: "/restaurant-list" }])}
        />
      )}
      <FlashMessage
        open={Boolean(message.type)}
        severity={message.type}
        alertMessage={message.message}
        handleClose={toggleSnackbar}
      />
      {isLoggedIn ? <Header /> : <LoginHeader showIcon />}

      <Box
        sx={{
          boxSizing: "border-box",
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: topBarOffset(20), md: topBarOffset(28) },
          pb: 6,
          display: "flex",
          flexDirection: "column",
          gap: "36px",
        }}
      >
        {showPickupHome ? (
          <PickupHome
            isPickup={isPickup}
            deliveryHere={!noDelivery}
            location={location}
            onOrder={orderForPickup}
            onUseAddress={switchToAddress}
            onMessage={showMessage}
          />
        ) : (
        <>
        <Box component="header">
          <Box component="h1" sx={{ ...TYPE.pageTitle, fontSize: { xs: 28, sm: 34 }, m: 0, color: C.ink }}>
            {firstName ? `${partOfDay}, ${firstName}` : partOfDay}
          </Box>
        </Box>

        {/* Order (Vivek, 2026-10-29): mood, deals, most ordered, the list, then Order again. */}
        {!search && (
          <>
            <CuisineRow
              choices={choices}
              selectedKey={choiceKey}
              onSelect={setChoiceKey}
              showCatering={restaurants.some((r) => r.hasCatering)}
              onCatering={() => navigate("/catering")}
            />

            <DealBanners
              promos={bannerPromos}
              rewardsPoints={isLoggedIn && rewardsBalance > 0 ? rewardsBalance : undefined}
              seeAllTo="/deals"
              eyebrowFor={(p) => (p.restaurantId ? p.restaurantName || p.name : p.name)}
              onOpenPromo={(p) => openRestaurant({ _id: p.restaurantId, name: p.restaurantName, slug: p.restaurantSlug })}
              sectionSx={{}}
            />

            <MostOrderedNearby
              restaurantIds={restaurants.map((r) => String(r._id))}
              city={town}
              onOpenDish={(dish) =>
                openRestaurant(
                  { _id: dish.restaurantId, name: dish.restaurantName, slug: dish.restaurantSlug },
                  { search: `?item=${encodeURIComponent(dish._id)}` }
                )
              }
            />
          </>
        )}

        <AllRestaurants
          title={gridTitle}
          restaurants={shown}
          dealCounts={dealCounts}
          filters={{ ...filters, openNow: openNowOnly }}
          onFilter={onFilter}
          showFavourites={isLoggedIn}
          sort={sort}
          onSort={setSort}
          onClear={clearFilters}
          onOpen={openRestaurant}
          onMessage={showMessage}
        />

        {!search && <OrderAgain onOpenRestaurant={openRestaurant} />}
        </>
        )}
      </Box>

      <ClearCart
        isVisible={clearModal}
        toggleModal={toggleClearCart}
        action={navigateClearCart}
      />
    </Box>
  );
}

export default Restaurants;
